import { useState } from 'react';
import { IoTriangle } from 'react-icons/io5';
import { BsStars } from 'react-icons/bs';
import { useRouter } from 'next/router';
import { V2Networks } from '@shared/utils/config/networkAddresses';
import { ZERO_ADDRESS } from '@shared/utils/config/networks';
import { toSpecialFixed } from '@/lib/misc/utils';
import { Button } from '@/components/ui/button';
import { getNetworkLogo } from '@/lib/misc/tokenImageHelper';
import { wBTCAddresses, ChainLinkAddresses, EthAddresses, StableTokenAddresses } from '@/hooks/explore/tokenAddresses';
import CustomDialog from '@/components/ui/customDialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import TokenImage from '@/components/shared/TokenImage';
import SlidingModal from '../home/mobile/SlidingModal';
import useAppDetails from '@/hooks/utilities/useAppDetails';
import useGetStableCoinData from '@/hooks/explore/useGetStableCoinTokenData';
interface Props {
  isMobile?: boolean;
  isModalOpen: boolean;
  toggleModal: () => void;
  token: any;
  onTokenClick?: (token: any) => void;
  change?: number;
  isStable?: boolean;
}
export default function NormieAssetModal({
  isMobile = false,
  isModalOpen,
  toggleModal,
  token,
  onTokenClick,
  change = 0,
  isStable = false
}: Props) {
  const router = useRouter();
  const [showInfo, setShowInfo] = useState(true);
  const {
    setMobileMode,
    setMobileModeMetadata
  } = useAppDetails();
  const tokenItem = token?.token;
  const {
    data: stableCoinData
  } = useGetStableCoinData();
  const [showDisclaimer, setShowDisclaimer] = useState(tokenItem?.name?.includes('BTC'));
  const tokenImage = isStable ? `/static/tokenlogos/${token.name}.png` : tokenItem?.address == ZERO_ADDRESS || tokenItem?.symbol == 'WETH' ? getNetworkLogo(Array.isArray(tokenItem?.networkId) ? tokenItem?.networkId[tokenItem?.networkId.length - 1] : tokenItem?.networkId) : tokenItem?.info?.imageThumbUrl || tokenItem?.imageSmallUrl || tokenItem?.info?.imageLargeUrl;
  const changeUp = toSpecialFixed(change * 100, 2)?.value;
  const changeDown = toSpecialFixed(Math.abs(change * 100), 2).value;
  const tokenPrice = isStable ? '1.0' : toSpecialFixed(token?.prices?.[0].priceUsd || token?.priceUSD || 0, 2).value;
  const tokenName = isStable ? token.name.toUpperCase() : tokenItem?.name?.includes('BTC') ? tokenItem?.name : tokenItem?.name.replace('Wrapped', '');
  const tokenSymbol = isStable ? token.name.toUpperCase() : tokenItem?.symbol?.replace('W', '');
  const tokenAddressMap = {
    BTC: wBTCAddresses,
    LINK: ChainLinkAddresses,
    ETH: EthAddresses,
    USDT: StableTokenAddresses.Usdt,
    DAI: StableTokenAddresses.Dai,
    USDC: StableTokenAddresses.Usdc
  };
  const iterableTokens = Object.keys(tokenAddressMap).find(key => tokenName?.includes(key) || tokenSymbol?.includes(key)) ? tokenAddressMap[Object.keys(tokenAddressMap).find(key => tokenName?.includes(key) || tokenSymbol?.includes(key))] : wBTCAddresses;
  const handleClick = (tokenAddress: string, tokenNetwork: number) => {
    if (onTokenClick) {
      if (isStable) {
        const stableToken = stableCoinData?.find(token => token?.token.address?.toLowerCase() === tokenAddress?.toLowerCase());
        onTokenClick(stableToken.token);
      } else {
        tokenItem.address = tokenAddress;
        tokenItem.networkId = tokenNetwork;
        onTokenClick(tokenItem);
      }
    } else {
      if (isMobile) {
        setMobileMode('asset');
        setMobileModeMetadata({
          networkId: tokenNetwork,
          address: tokenAddress,
          prev_m_mode: 'search'
        });
        router.push(`/home?m_mode=asset&networkId=${tokenNetwork}&address=${tokenAddress}`, undefined, {
          shallow: true
        });
      } else {
        router.push(`/asset?address=${tokenAddress}&networkId=${tokenNetwork}`);
      }
    }
  };
  const renderContent = () => {
    return <div className='text-[16px] overflow-y-auto h-fit-content' data-sentry-component="renderContent" data-sentry-source-file="NormieAssetModal.tsx">
        <div>
          {showDisclaimer && tokenName?.includes('BTC') && <div className='mb-2 rounded-xl overflow-hidden pt-6 p-6 pb-0'>
              <p className='text-genius-cream text-[16px]'>Disclaimer</p>
              <p className='text-genius-cream/50 text-[12px] leading-4 my-1'>{tokenName}</p>
              <p className='text-genius-cream/50 text-[12px] leading-4'>
                Please be aware that {tokenName} on Genius is “Wrapped” meaning it is a synthetic
                version of the asset that tracks the price 1:1. If you’re looking for exposure to
                Bitcoin as an asset, you will not notice a difference. However, if you’re trying to
                buy Bitcoin in order to send or withdraw BTC to other users on the Bitcoin
                blockchain, trading the wrapped version on Genius will not allow you to do so.
              </p>
              <div className='p-3.5 py-2 flex items-center justify-end'>
                <div className='flex items-center'>
                  <Button onClick={() => setShowDisclaimer(false)} className='gradient-genius-sides bg-genius-blue md:hover:opacity-75 transition-opacity transition-colors text-[12px] text-genius-cream underline rounded-full'>
                    Got it
                  </Button>
                </div>
              </div>
            </div>}
          <div className={`rounded-xl overflow-hidden px-4 lg:pt-6 ${showDisclaimer && tokenName?.includes('BTC') ? 'lg:pt-0' : ''}`}>
            <div className='flex items-center w-full mb-2 p-3'>
              <TokenImage mainImageSrc={tokenImage?.replace('large', 'small')} badgeImageSrc={'/static/tokenlogos/gen.png'} size={24} badgeSize={12} data-sentry-element="TokenImage" data-sentry-source-file="NormieAssetModal.tsx" />
              <div className='w-2/5 ta-l pl-4'>
                <p className='text-genius-cream text-[16px]'>{tokenName}</p>
                <p className='text-genius-cream/50 text-[12px]'>{tokenSymbol}</p>
              </div>
              <div className='flex flex-col text-right text-wrap w-[55%]'>
                <p className='text-[16px] text-genius-cream text-right text-wrap'>${tokenPrice}</p>
                {!isStable ? <div className='flex gap-1 items-center justify-end text-right text-[12px]'>
                    {change >= 0 ? <div className='flex items-center gap-1 text-right'>
                        <IoTriangle color='#34D399' size={12} />
                        <div className='text-gray-400 text-right'>{`${changeUp}%`}</div>
                      </div> : <div className='flex items-center gap-1 text-right mt-1'>
                        <IoTriangle color='#FF5B5B' size={12} className='rotate-180' />
                        <div className='text-red-400 text-right'>{`${changeDown}%`}</div>
                      </div>}
                  </div> : <div className='flex gap-1 items-center justify-end text-right text-[12px] text-genius-cream/50'>
                    Stable
                  </div>}
              </div>
            </div>
            <p className='text-genius-cream/50 text-[12px] ta-l m-0 px-2 pb-2'>
              {showInfo ? `Very soon, Genius will enable you to spend all your ` + tokenSymbol + ` as one, regardless of which
                            network it’s on. We call this:` : `There are multiple versions of ` + tokenSymbol + ` on different networks. We recommend selecting the version of the network you are trading on to keep your fees low.`}
            </p>
          </div>
          {showInfo && <div className='gradient-genius-sides p-3.5 py-2 flex items-center justify-between'>
              <div className='flex items-center'>
                <p className='flex items-center text-genius-cream text-[16px]'>
                  <BsStars />
                  Magic Spend
                </p>
                <div className='bg-black ml-2 text-[10px] text-genius-cream rounded-full p-1 px-2'>
                  Coming Soon
                </div>
              </div>
              <div className='flex items-center'>
                <Button onClick={() => setShowInfo(false)} className='ml-2 bg-genius-blue md:hover:opacity-75 transition-opacity text-[12px] text-genius-cream rounded-full'>
                  Got it
                </Button>
              </div>
            </div>}
          <div className='mt-4'>
            {Object.values(iterableTokens).map((token: any, index: number) => {
            return <div onClick={() => handleClick(token?.address, token?.network)} key={index} className='flex items-center mb-2 h-[70px] px-6 hover:bg-genius-blue transition-colors cursor-pointer'>
                  <TokenImage mainImageSrc={tokenImage?.replace('large', 'small')} badgeImageSrc={getNetworkLogo(token?.network)} size={24} badgeSize={12} />
                  <div className='pl-4'>
                    <p className='text-genius-cream text-[16px]'>{tokenName}</p>
                    <p className='text-genius-cream/50 text-[12px] ta-l flex items-center space-x-2'>
                      {tokenSymbol}
                      <span className='ml-1'>on {V2Networks[token?.network].name}</span>
                    </p>
                  </div>
                  <div className='flex-1 text-right'>
                    <div className='text-genius-cream'>${tokenPrice}</div>
                    {!isStable ? <div className='flex gap-1 items-center justify-end text-right text-[12px]'>
                        {change >= 0 ? <div className='flex items-center gap-1 text-right'>
                            <IoTriangle color='#34D399' size={12} />
                            <div className='text-gray-400 text-right'>{`${changeUp}%`}</div>
                          </div> : <div className='flex items-center gap-1 text-right mt-1'>
                            <IoTriangle color='#FF5B5B' size={12} className='rotate-180' />
                            <div className='text-red-400 text-right'>{`${changeDown}%`}</div>
                          </div>}
                      </div> : <div className='flex gap-1 items-center justify-end text-right text-[12px] text-genius-cream/50'>
                        Stable
                      </div>}
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>;
  };
  return isMobile ? <SlidingModal heightFit isOpen={isModalOpen} onClose={toggleModal} data-sentry-element="SlidingModal" data-sentry-component="NormieAssetModal" data-sentry-source-file="NormieAssetModal.tsx">
      {renderContent()}
    </SlidingModal> : <CustomDialog isOpen={isModalOpen} toggleModal={toggleModal} className={'p-0 '} data-sentry-element="CustomDialog" data-sentry-component="NormieAssetModal" data-sentry-source-file="NormieAssetModal.tsx">
      <ScrollArea className='h-full' data-sentry-element="ScrollArea" data-sentry-source-file="NormieAssetModal.tsx">{renderContent()}</ScrollArea>
    </CustomDialog>;
}